import { pusher } from '/app/plugins/pusher';
import { ensureNotificationToken, refreshNotificationToken } from '/app/composables/notification-token';
import { tryOnScopeDispose } from '@vueuse/core';
import { watch, toRef, computed, toValue } from 'vue';

// Global tracking of channel usage across all components.
//
// Keyed by channel name, holding one token per live `useChannel()` call.
// Pusher returns the *same* channel object for every `subscribe()` of a name,
// so counting channel objects would collapse every consumer of a shared
// channel (`discussion-topic`, say) into a single entry — and the first
// component to unmount would unsubscribe the channel out from under the rest.
const channels = new Map();

const needsAuth = (channelName) =>
    channelName.startsWith('private-') || channelName.startsWith('presence-');

const releaseChannel = (channelName, token) => {
    const subscribers = channels.get(channelName);

    if (!subscribers) return;

    subscribers.delete(token);

    if (subscribers.size === 0) {
        channels.delete(channelName);
        pusher.unsubscribe(channelName);
    }
};

const retainChannel = (channelName, token) => {
    if (!channelName || channelName.includes('undefined')) {
        if (channelName?.includes('undefined')) {
            console.warn('Attempted to subscribe to channel with undefined in name:', channelName);
        }

        return null;
    }

    const channel = pusher.subscribe(channelName);

    if (!channels.has(channelName)) {
        channels.set(channelName, new Set());
    }

    channels.get(channelName).add(token);

    return channel;
};

export const useChannel = (channelName) => {
    // Normalize channelName to handle ref, computed, getter, or plain value
    const reactiveChannelName = computed(() => toValue(channelName));

    // Identifies this `useChannel()` call in the usage registry above.
    const token = Symbol('channel-subscriber');

    // Track events registered by this instance
    const events = new Map();

    let currentChannel = null;
    let isChannelReady = false;
    let detach = () => {};

    // Bumped on every channel-name change and on scope dispose, so an
    // authorisation that resolves late knows it is stale and bails out.
    let generation = 0;

    const attach = (name, id, allowRetry = true) => {
        const channel = retainChannel(name, token);

        if (!channel) return;

        // The name changed (or the scope died) while auth was in flight.
        if (id !== generation) {
            releaseChannel(name, token);

            return;
        }

        currentChannel = channel;

        const onSucceeded = () => {
            if (isChannelReady) return;

            isChannelReady = true;
            events.forEach((callback, event) => channel.bind(event, callback));
        };

        // Pusher does not retry a rejected authorisation, so a channel that
        // fails auth stays dead until the page reloads. An expired notification
        // token is the usual cause: drop it, fetch a fresh one and resubscribe
        // — once, so a genuine 403 cannot loop.
        const onError = (payload) => {
            if (!allowRetry || ![401, 403].includes(payload?.status)) return;

            refreshNotificationToken()
                .then(() => {
                    if (id !== generation) return;

                    detach();
                    attach(name, id, false);
                })
                .catch(() => {});
        };

        channel.bind('pusher:subscription_succeeded', onSucceeded);
        channel.bind('pusher:subscription_error', onError);

        detach = () => {
            events.forEach((callback, event) => channel.unbind(event, callback));
            channel.unbind('pusher:subscription_succeeded', onSucceeded);
            channel.unbind('pusher:subscription_error', onError);

            releaseChannel(name, token);

            currentChannel = null;
            isChannelReady = false;
            detach = () => {};
        };

        // An already-subscribed channel will not re-emit `subscription_succeeded`.
        if (channel.subscribed) onSucceeded();
    };

    // Watch for changes in the channel name
    watch(reactiveChannelName, (newChannelName) => {
        generation += 1;
        detach();

        if (!newChannelName) return;

        const id = generation;

        if (needsAuth(newChannelName)) {
            ensureNotificationToken()
                .then(() => attach(newChannelName, id))
                .catch(() => {});

            return;
        }

        attach(newChannelName, id);
    }, { immediate: true }); // Execute immediately to handle the initial channel name

    const listen = (event, callback) => {
        // Normalize event to handle ref, computed, getter, or plain value
        const reactiveEvent = toRef(event);

        // Watch for changes in the event
        watch(reactiveEvent, (newEvent, oldEvent) => {
            // Unbind old event if it exists and channel is available
            if (oldEvent && events.has(oldEvent)) {
                currentChannel?.unbind(oldEvent, events.get(oldEvent));
                events.delete(oldEvent);
            }

            if (!newEvent) return;

            // Store it either way: a channel that is still authorising binds
            // everything in `events` once it is ready.
            events.set(newEvent, callback);

            if (currentChannel && isChannelReady) {
                currentChannel.bind(newEvent, callback);
            }
        }, { immediate: true }); // Execute immediately to bind the initial event
    };

    tryOnScopeDispose(() => {
        generation += 1;
        detach();
    });

    return {
        listen,
        pusher,
        channel: computed(() => currentChannel),
    };
}
